import { DynamicStandaloneArticleDate } from '@/components/ArticleDate';
import { DynamicStandaloneByline } from '@/components/Byline';
import { DynamicStandaloneImage } from '@/components/Image';
import { DynamicStandaloneJwVideoArticle } from '@/components/JwVideo/Article';
import { DynamicStandaloneSaveArticleButton } from '@/components/SaveArticleButton';
import { DynamicStandaloneTag } from '@/components/Tag';
import { useStable, useStableProps } from '@/hooks/useStable';
import { ClassNameProvider, componentTheme } from '@/styles/ArticleHeader';
import { StandaloneComponent } from '@/types/component';
import { StandaloneArticleHeaderProps } from 'base/components/ArticleHeader';
import { ArticleHeader } from 'base/components/ArticleHeader/ArticleHeader';
import { ArticleBadgeData } from 'lib/labrador/articleBadge';
import { cn } from 'lib/utils';
import { mergeOptions } from 'lib/utils/merge';
import mergeProps from 'lib/utils/mergeProps';
import { useMemo } from 'react';

export interface MabraStandaloneArticleHeaderProps extends StandaloneArticleHeaderProps {
  badge?: ArticleBadgeData;
}

export const MabraStandaloneArticleHeader: StandaloneComponent<MabraStandaloneArticleHeaderProps> = ({
  bylines,
  category,
  description,
  headline,
  image,
  tags,
  video,
  datetime,
  badge,
  options,
  ...props
}) => {
  const {
    $bylines: bylinesProps,
    $date: dateProps,
    $category: categoryProps,
    $description: descriptionProps,
    $headline: headlineProps,
    $media,
    $tags: tagsProps,
    ...baseProps
  } = options ?? {};

  const { $tag: $categoryTag, ...$category } = categoryProps ?? {};
  const { $tag, ...$tags } = tagsProps ?? {};

  const memoTags = useMemo(
    () =>
      tags?.map((tag, index) => {
        return (
          <DynamicStandaloneTag
            key={index}
            link={{ href: `/etikett/${tag.value}` }}
            {...mergeProps(
              {
                options: mergeOptions(
                  {
                    variant: 'text',
                    theme: componentTheme('tag'),
                  },
                  $tag
                ),
              },
              tag
            )}
          />
        );
      }),
    [tags, $tag]
  );

  const stableImageProps = useStableProps(
    {
      options: {
        fill: true,
        priority: true,
        $caption: {
          $byline: {
            className: cn(
              'text-mb-headline-2xs',
              '!text-mb-gray-600',
              'not-italic',
              'sm:text-right',
              badge ? 'mt-1' : '-mt-2',
              'sm:-mt-2'
            ),
          },
          $text: { className: 'h-0 !mb-0' },
        },
      },
    },
    image
  );

  const memoCategoryTag = useMemo(
    () => (
      <DynamicStandaloneTag
        value={category?.value}
        link={{ href: category?.url || `etikett/${category?.value}` }}
        options={mergeOptions(
          {
            variant: 'text',
            theme: componentTheme('tag'),
          },
          $categoryTag
        )}
      />
    ),
    [$categoryTag, category?.url, category?.value]
  );

  const imageComponent = image?.src && <DynamicStandaloneImage {...stableImageProps} />;

  const videoComponent = video && (
    <DynamicStandaloneJwVideoArticle
      {...video}
      options={mergeOptions(video.options, {
        className: 'border-y-0 !mb-0',
      })}
    />
  );

  const media = imageComponent || videoComponent || null;

  const stableProviderValue = useStable({ ...baseProps, variant: 'second' });

  return (
    <ClassNameProvider value={stableProviderValue}>
      <ArticleHeader {...baseProps} {...props}>
        {tags && tags.length > 0 && <ArticleHeader.Tags {...$tags}>{memoTags}</ArticleHeader.Tags>}
        {headline && <ArticleHeader.Headline {...headlineProps}>{headline}</ArticleHeader.Headline>}
        {category?.value && <ArticleHeader.Category {...$category}>{memoCategoryTag}</ArticleHeader.Category>}
        {datetime?.publishedDatetime && (
          <ArticleHeader.Date {...dateProps}>
            <DynamicStandaloneArticleDate {...datetime} />
          </ArticleHeader.Date>
        )}
        {bylines && bylines?.length > 0 && (
          <ArticleHeader.Bylines {...bylinesProps}>
            {bylines?.map((byline, index) => (
              <DynamicStandaloneByline {...byline} key={index} />
            ))}
          </ArticleHeader.Bylines>
        )}
        {media && <ArticleHeader.Media {...$media}>{media}</ArticleHeader.Media>}

        {description && <ArticleHeader.Description {...descriptionProps}>{description}</ArticleHeader.Description>}
      </ArticleHeader>
      <DynamicStandaloneSaveArticleButton />
    </ClassNameProvider>
  );
};

export default MabraStandaloneArticleHeader;
